import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function TableOfContents({ sections, activeSection }) {
  return (
    <div className="lg:w-64 xl:w-72 shrink-0 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 lg:sticky lg:top-32 lg:self-start max-h-[calc(100vh-10rem)] overflow-y-auto">
      <h3 className="font-bold text-lg mb-4 text-primary-500 dark:text-primary-400">Contents</h3>
      <nav className="space-y-1">
        {sections.map(section => (
          <div key={section.id}>
            <a
              href={`#${section.id}`}
              className={`block py-1.5 px-3 rounded-lg transition-colors text-sm ${
                activeSection === section.id
                  ? 'bg-primary-400/10 text-primary-500 dark:text-primary-400 font-medium'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {section.title}
            </a>
            {section.children && (
              <div className="ml-3 mt-0.5 space-y-0.5">
                {section.children.map(child => (
                  <a
                    key={child.id}
                    href={`#${child.id}`}
                    className={`block py-1 px-3 rounded-lg transition-colors text-xs ${
                      activeSection === child.id
                        ? 'text-primary-500 dark:text-primary-400 font-medium'
                        : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-400'
                    }`}
                  >
                    {child.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

/**
 * The shell both documentation sets share: header, version switcher, sticky
 * table of contents and the scroll spy that drives it.
 *
 * Two sets exist because version 3 is a rewrite rather than an update — the
 * tabs moved, several are new, and a few are gone — so a single page could only
 * have described one of them correctly. The switcher is what stops someone
 * running 2.8.7 reading about a screen they do not have.
 */
export default function DocsLayout({ version, sections, intro, children }) {
  const [activeSection, setActiveSection] = useState(sections[0]?.id);

  useEffect(() => {
    const allIds = [];
    sections.forEach(s => {
      allIds.push(s.id);
      if (s.children) s.children.forEach(c => allIds.push(c.id));
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { threshold: 0.2, rootMargin: '-80px 0px -60% 0px' }
    );

    allIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  const isLegacy = version === '2';

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4 sm:gap-6">
          <img
            src="/images/avatars/docs_avatar.png"
            alt=""
            className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 object-contain drop-shadow-lg shrink-0"
          />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
              Documentation
            </h1>
            <p className="text-slate-600 dark:text-slate-400 max-w-2xl">{intro}</p>
          </div>
        </div>

        {/* Version switcher */}
        <div className="mb-10 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-1">
            <Link
              to="/docs"
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                !isLegacy
                  ? 'bg-primary-400 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              Version 3.1
            </Link>
            <Link
              to="/docs/v2"
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                isLegacy
                  ? 'bg-primary-400 text-white'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              Version 2.8
            </Link>
          </div>
          {isLegacy && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              You are reading the archived documentation for version 2.8.7.
            </p>
          )}
        </div>

        {isLegacy && (
          <div className="mb-10 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-5">
            <h2 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">
              This describes version 2.8.7
            </h2>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Version 3.0 is a ground-up rewrite. Several tabs moved, some were
              replaced, and a number of screens described here no longer exist
              under the same names. If you are running 3.0 or later, read the{' '}
              <Link to="/docs" className="underline">current documentation</Link>{' '}
              instead. This page is kept for anyone still on 2.8.7.
            </p>
          </div>
        )}

        <div className="lg:flex lg:gap-10">
          {/* Table of contents — desktop */}
          <div className="hidden lg:block">
            <TableOfContents sections={sections} activeSection={activeSection} />
          </div>

          {/* Table of contents — mobile */}
          <details className="lg:hidden mb-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <summary className="p-4 font-medium text-slate-900 dark:text-white cursor-pointer">
              Table of Contents
            </summary>
            <div className="px-4 pb-4">
              <TableOfContents sections={sections} activeSection={activeSection} />
            </div>
          </details>

          {/* Main content */}
          <div className="flex-1 max-w-4xl space-y-16">{children}</div>
        </div>
      </div>
    </div>
  );
}
